import { useMemo, useState } from "react";
import { Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DocumentChannel, sendPatientDocument } from "@/lib/campaigns";

type PatientContact = { id?: string; nome?: string; email?: string; telefone?: string; celular?: string };

type Props = {
  entidade: "documentos_emitidos" | "contratos_paciente" | "receituario" | "agendamentos" | "planos_tratamento" | "ordem_servico" | "odontograma";
  documentoId: string;
  clinicaId?: string;
  titulo: string;
  paciente?: PatientContact;
  disabled?: boolean;
  compact?: boolean;
};

const modes: Array<{ value: "email" | "whatsapp" | "ambos"; label: string; icon: typeof Mail }> = [
  { value: "email", label: "E-mail", icon: Mail },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { value: "ambos", label: "Ambos", icon: Send },
];

function channelsFor(mode: "email" | "whatsapp" | "ambos"): DocumentChannel[] {
  return mode === "ambos" ? ["email", "whatsapp"] : [mode];
}

export function EnviarDocumentoPaciente({ entidade, documentoId, clinicaId, titulo, paciente, disabled, compact }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"email" | "whatsapp" | "ambos">("ambos");
  const [sending, setSending] = useState(false);
  const email = String(paciente?.email || "").trim();
  const phone = String(paciente?.telefone || paciente?.celular || "").trim();
  const canSend = useMemo(() => {
    if (mode === "email") return Boolean(email);
    if (mode === "whatsapp") return Boolean(phone);
    return Boolean(email && phone);
  }, [email, phone, mode]);

  const changeOpen = (value: boolean) => {
    if (value) setMode(email && phone ? "ambos" : email ? "email" : "whatsapp");
    setOpen(value);
  };

  const submit = async () => {
    if (!canSend || sending) return;
    setSending(true);
    try {
      const result = await sendPatientDocument({
        entidade,
        documento_id: documentoId,
        clinica_id: clinicaId,
        canais: channelsFor(mode),
        request_id: globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
      const delivered = result.canais.map((channel) => channel === "email" ? "e-mail" : "WhatsApp").join(" e ");
      const ignored = Object.values(result.ignorados || {}).filter(Boolean);
      const sentNow = Number(result.envio_imediato?.enviados || 0);
      const immediateErrors = Number(result.envio_imediato?.erros || 0);
      const providerError = result.envio_imediato?.detalhes?.map((item) => item.erro).find(Boolean) || result.envio_imediato?.motivo;
      let closeAfterSubmit = false;
      if (sentNow > 0 && sentNow === result.canais.length) {
        toast.success(`Documento encaminhado agora${delivered ? ` por ${delivered}` : ""}.`);
        closeAfterSubmit = true;
      } else if (sentNow > 0) {
        toast.warning(`${sentNow} canal(is) aceitaram o envio. Os demais não foram enviados e ficaram para nova tentativa.`);
      } else if (immediateErrors > 0 || result.envio_imediato?.motivo) {
        toast.error(`Não enviado agora${providerError ? `: ${providerError}` : ""}. A tentativa ficou registrada para novo processamento.`);
      } else {
        toast.info(`${result.duplicado ? "Envio já solicitado" : "Aguardando o processador"}${delivered ? ` para ${delivered}` : ""}. Ainda não há confirmação de envio.`);
      }
      if (ignored.length) toast.info(ignored.join(". "));
      if (closeAfterSubmit) setOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível enviar o documento.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size={compact ? "icon" : "sm"} disabled={disabled || (!email && !phone)} title={!email && !phone ? "Paciente sem email ou WhatsApp" : "Enviar agora ao paciente"}>
          <Send className={compact ? "h-4 w-4" : "mr-2 h-4 w-4"} />
          {!compact && "Enviar agora"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enviar documento ao paciente</DialogTitle>
          <DialogDescription>{titulo}. O sistema gera um PDF individual e o envia como anexo. O envio fica registrado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <strong>{paciente?.nome || "Paciente"}</strong>
            <p className="mt-1 text-muted-foreground">E-mail: {email || "não cadastrado"}</p>
            <p className="text-muted-foreground">WhatsApp: {phone || "não cadastrado"}</p>
          </div>
          <div className="space-y-2">
            <Label>Canal de envio</Label>
            <div className="grid grid-cols-3 gap-2">
              {modes.map(({ value, label, icon: Icon }) => {
                const unavailable = value === "email" ? !email : value === "whatsapp" ? !phone : !email || !phone;
                return <Button key={value} type="button" variant={mode === value ? "default" : "outline"} className="h-auto min-h-16 flex-col gap-1 px-2" disabled={unavailable} onClick={() => setMode(value)}><Icon className="h-4 w-4" /><span className="text-xs">{label}</span></Button>;
              })}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">O WhatsApp recebe o PDF anexado. No e-mail, o mesmo PDF segue como anexo. O sistema respeita os consentimentos e bloqueios do paciente.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => changeOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSend || sending}><Send className="mr-2 h-4 w-4" />{sending ? "Tentando enviar..." : "Enviar agora"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
